import type { AdminSelf } from '@saay/api-client';
import { KpiCard, ListRow, Notice, type Tone } from '@saay/blocks';
import { formatCount } from '@saay/formatting';
import { buttonVariants } from '@saay/shadcn/components/button';
import { cn } from '@saay/shadcn/lib/utils';
import {
  CreditCardIcon,
  GaugeIcon,
  HashIcon,
  type LucideIcon,
  MapPinOffIcon,
  UsersIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router';

import type { PhysicalCardCityUsage, PhysicalCardNumberingUsage } from '../api.js';
import { DetailHeader } from '../components/detail-header.js';
import { QueryState, RequireArea, usePageTitle } from '../components/guard.js';
import { DARK_PILL, OUTLINE_PILL, SurfaceSection } from '../components/surface-section.js';
import { AccessDenied, EmptyState, Skeleton, Tabs } from '../kit.js';
import { physicalCardsPath } from '../nav.js';
import {
  canManageNumberingPolicy,
  type PhysicalCardsTab,
  physicalCardsTabs,
} from '../permissions.js';
import { useAllNumberingCities, useNumberingUsage } from '../queries.js';
import { CitiesPanel } from './physical-card-cities.js';
import { CITY_PARAM, EDIT_PARAM, type EditTarget } from './physical-card-config.js';
import { Digits, NumberingHistoryPanel } from './physical-card-numbering.js';
import { PhysicalCardPrintPanel } from './physical-card-print.js';
import { NumberingSettingsPanel } from './physical-card-settings.js';

/**
 * `/physical-cards` «کارت‌های فیزیکی» (D-117): one place for everything card-related, a full page
 * with a sticky header and one tab per question. «خلاصه» (is everything OK, what needs me), «شهرها»
 * (usage and configuration of every city in one table, K9), «تنظیمات شماره‌گذاری» (series and
 * provinces, AC §31.6), «چاپ کارت» (the print batches, D-118), and «سابقه» (the versions). Each row action of the
 * Super Admin is a small dialog or an inline editor; a «نیاز به اقدام» link opens the exact one. A tab the role cannot use is not shown. Counts only (K8, K9):
 * no buyer, mobile, or card number reaches these tabs except the print file.
 */

const TAB_LABEL: Readonly<Record<PhysicalCardsTab, string>> = {
  cities: 'شهرها',
  history: 'سابقه',
  print: 'چاپ کارت',
  settings: 'تنظیمات شماره‌گذاری',
  summary: 'خلاصه',
};

/** The query keys this page owns; any other (for example the dev mock's) rides along. */
const OWN_PARAMS = ['province', 'state', 'q', CITY_PARAM, EDIT_PARAM] as const;

function carriedSearch(search: string, extra: Readonly<Record<string, string>> = {}): string {
  const params = new URLSearchParams(search);
  for (const key of OWN_PARAMS) params.delete(key);
  for (const [key, value] of Object.entries(extra)) params.set(key, value);
  const text = params.toString();
  return text === '' ? '' : `?${text}`;
}

/** A link to a tab of this page that keeps the foreign query (the dev mock's role). */
function useTabHref() {
  const { search } = useLocation();
  return (tab: PhysicalCardsTab, extra?: Readonly<Record<string, string>>) => ({
    pathname: physicalCardsPath(tab),
    search: carriedSearch(search, extra),
  });
}

/** `/physical-card-exports` (the old print page) opens the «چاپ کارت» tab. */
export function PhysicalCardExportRedirect() {
  const { search } = useLocation();
  return <Navigate replace to={{ pathname: physicalCardsPath('print'), search }} />;
}

export function PhysicalCardsPage() {
  usePageTitle('کارت‌های فیزیکی');
  const { tab } = useParams();
  return (
    <RequireArea area="physicalCards">
      {(self) => <PhysicalCardsView requested={tab ?? 'summary'} self={self} />}
    </RequireArea>
  );
}

function PhysicalCardsView({
  requested,
  self,
}: {
  readonly requested: string;
  readonly self: AdminSelf;
}) {
  const navigate = useNavigate();
  const href = useTabHref();
  const tabs = physicalCardsTabs(self);
  const tab = tabs.find((key) => key === requested);
  const first = tabs[0];
  if (first === undefined) {
    return <AccessDenied />;
  }
  if (tab === undefined) {
    return <Navigate replace to={href(first)} />;
  }
  return (
    <section aria-labelledby="physical-cards-title" className="flex min-w-0 flex-col gap-4">
      <DetailHeader title="کارت‌های فیزیکی" titleId="physical-cards-title" />
      <Tabs<PhysicalCardsTab>
        label="بخش‌های کارت‌های فیزیکی"
        onChange={(next) => void navigate(href(next))}
        panel={<TabPanel self={self} tab={tab} />}
        tabs={tabs.map((key) => ({ key, label: TAB_LABEL[key] }))}
        value={tab}
      />
    </section>
  );
}

function TabPanel({ self, tab }: { readonly self: AdminSelf; readonly tab: PhysicalCardsTab }) {
  switch (tab) {
    case 'cities':
      return <CitiesPanel manage={canManageNumberingPolicy(self)} />;
    case 'settings':
      return <NumberingSettingsPanel self={self} />;
    case 'print':
      return <PhysicalCardPrintPanel />;
    case 'history':
      return <NumberingHistoryPanel />;
    case 'summary':
      return <SummaryPanel manage={canManageNumberingPolicy(self)} />;
  }
}

// ---------------------------------------------------------------------------------------------
// «خلاصه»

/** What a «نیاز به اقدام» row asks for; the Super Admin's link opens that exact action. */
type Fix = 'activate' | 'cap' | 'code' | 'series';

export interface AttentionItem {
  /** The city the fix is for (`cap`, `code`). */
  readonly cityId?: string;
  readonly description?: string;
  readonly fix: Fix;
  readonly icon: LucideIcon;
  readonly key: string;
  /** Where a reader without the editor looks instead (`cities` filters by `state`). */
  readonly look: { readonly state?: string; readonly tab: 'cities' | 'settings' };
  readonly title: string;
  readonly tone: Tone;
}

const FIX_LABEL: Readonly<Record<Fix, string>> = {
  activate: 'انتخاب سری',
  cap: 'افزایش سقف',
  code: 'تعریف کد',
  series: 'سری جدید',
};

/** The manager's link of a row: the tab, filtered to the city, with the action to open once. */
export function fixLink(item: AttentionItem): {
  readonly extra: Readonly<Record<string, string>>;
  readonly tab: 'cities' | 'settings';
} {
  const edit: EditTarget = item.fix;
  if (item.fix === 'activate' || item.fix === 'series') {
    return { extra: { [EDIT_PARAM]: edit }, tab: 'settings' };
  }
  return {
    extra: {
      ...(item.fix === 'code' ? { state: 'NONE' } : {}),
      ...(item.cityId === undefined ? {} : { [CITY_PARAM]: item.cityId }),
      [EDIT_PARAM]: edit,
    },
    tab: 'cities',
  };
}

function people(count: number): string {
  return `${formatCount(count)} خریدار`;
}

function capLine(row: PhysicalCardCityUsage): string {
  return `${formatCount(row.liveCount)} از ${formatCount(row.issuanceCap)} کارت`;
}

/**
 * The «نیاز به اقدام» rows from K8 and the K9 rows near or at their cap, most urgent first: no
 * active series, full cities, cities without a code that have waiting buyers, a used-up serial
 * range, then cities near their cap. Plain words only; each row names the one fix.
 */
export function attentionItems(
  usage: PhysicalCardNumberingUsage,
  capRows: readonly PhysicalCardCityUsage[],
): AttentionItem[] {
  const items: AttentionItem[] = [];
  const byReason = usage.waiting.byReason;
  if (usage.series.length > 0 && usage.activeSeriesPrefix === null) {
    const waiting = byReason.NO_ACTIVE_SERIES ?? 0;
    items.push({
      description: 'تا یک سری فعال نشود، کارت جدید صادر نمی‌شود.',
      fix: 'activate',
      icon: HashIcon,
      key: 'no-active-series',
      look: { tab: 'settings' },
      title:
        waiting > 0
          ? `${people(waiting)} منتظرند چون سری فعالی انتخاب نشده`
          : 'سری فعالی انتخاب نشده',
      tone: 'clay',
    });
  }
  const full = capRows
    .filter((row) => row.capState === 'AT_CAP')
    .sort((left, right) => right.waitingBuyerCount - left.waitingBuyerCount);
  for (const row of full) {
    items.push({
      cityId: row.cityId,
      description: capLine(row),
      fix: 'cap',
      icon: GaugeIcon,
      key: `cap:${row.cityId}`,
      look: { state: 'AT_CAP', tab: 'cities' },
      title:
        row.waitingBuyerCount > 0
          ? `${people(row.waitingBuyerCount)} منتظرند چون سقف ${row.cityNameFa} پر شده`
          : `سقف ${row.cityNameFa} پر شده`,
      tone: 'clay',
    });
  }
  for (const city of usage.waiting.unconfiguredCities) {
    items.push({
      cityId: city.cityId,
      description: `${people(city.waitingBuyerCount)} منتظر کارت`,
      fix: 'code',
      icon: MapPinOffIcon,
      key: `code:${city.cityId}`,
      look: { state: 'NONE', tab: 'cities' },
      title: `${city.cityNameFa} کد شهر ندارد`,
      tone: 'clay',
    });
  }
  const exhausted = byReason.SERIAL_EXHAUSTED ?? 0;
  if (exhausted > 0) {
    items.push({
      description: 'یک سری جدید بسازید و فعالش کنید.',
      fix: 'series',
      icon: HashIcon,
      key: 'serial-exhausted',
      look: { tab: 'settings' },
      title: `${people(exhausted)} منتظرند چون شماره‌های سری فعال برای شهرشان تمام شده`,
      tone: 'clay',
    });
  }
  for (const row of capRows.filter((item) => item.capState === 'NEAR_CAP')) {
    items.push({
      cityId: row.cityId,
      description: capLine(row),
      fix: 'cap',
      icon: GaugeIcon,
      key: `near:${row.cityId}`,
      look: { state: 'NEAR_CAP', tab: 'cities' },
      title: `${row.cityNameFa} نزدیک سقف است`,
      tone: 'copper',
    });
  }
  return items;
}

/** At most this many rows; the rest are one «همهٔ شهرها» link. */
const ATTENTION_LIMIT = 6;

function AttentionList({
  capRows,
  manage,
  usage,
}: {
  readonly capRows: readonly PhysicalCardCityUsage[];
  readonly manage: boolean;
  readonly usage: PhysicalCardNumberingUsage;
}) {
  const href = useTabHref();
  const items = attentionItems(usage, capRows);
  const shown = items.slice(0, ATTENTION_LIMIT);
  const more = items.length - shown.length;
  return (
    <SurfaceSection id="physical-cards-attention" title="نیاز به اقدام">
      {items.length === 0 ? (
        <EmptyState description="کاری منتظر شما نیست." icon="check" title="همه‌چیز مرتب است" />
      ) : (
        <div aria-label="نیاز به اقدام" className="flex flex-col gap-2" role="list">
          {shown.map((item) => (
            <ListRow
              {...(item.description === undefined ? {} : { description: item.description })}
              end={
                <Link
                  className={cn(buttonVariants({ variant: 'outline' }), OUTLINE_PILL)}
                  to={
                    manage
                      ? href(fixLink(item).tab, fixLink(item).extra)
                      : href(
                          item.look.tab,
                          item.look.state === undefined ? {} : { state: item.look.state },
                        )
                  }
                >
                  {manage ? FIX_LABEL[item.fix] : 'مشاهده'}
                </Link>
              }
              icon={item.icon}
              key={item.key}
              role="listitem"
              title={item.title}
              tone={item.tone}
            />
          ))}
          {more > 0 ? (
            <Link className="self-start text-sm font-medium text-primary" to={href('cities')}>
              {`${formatCount(more)} مورد دیگر در «شهرها»`}
            </Link>
          ) : null}
        </div>
      )}
    </SurfaceSection>
  );
}

function SummaryKpis({
  capRows,
  usage,
}: {
  readonly capRows: readonly PhysicalCardCityUsage[];
  readonly usage: PhysicalCardNumberingUsage;
}) {
  const live =
    usage.series.reduce((total, series) => total + series.liveCount, 0) + usage.legacyNumberCount;
  const atCap = capRows.filter((row) => row.capState === 'AT_CAP').length;
  const nearCap = capRows.filter((row) => row.capState === 'NEAR_CAP').length;
  const figure = (value: ReactNode) => (
    <span className="text-2xl font-extrabold tabular-nums">{value}</span>
  );
  return (
    <section aria-label="خلاصهٔ کارت‌ها" className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
      <KpiCard
        {...(usage.legacyNumberCount > 0
          ? { foot: `شامل ${formatCount(usage.legacyNumberCount)} کارت قدیمی` }
          : {})}
        icon={CreditCardIcon}
        label="کارت فعال"
        tone="olive"
        value={figure(formatCount(live))}
      />
      <KpiCard
        icon={UsersIcon}
        label="منتظر کارت"
        tone="clay"
        value={figure(formatCount(usage.waiting.buyerCount))}
      />
      <KpiCard
        {...(atCap + nearCap > 0
          ? { foot: `${formatCount(atCap)} پر، ${formatCount(nearCap)} نزدیک` }
          : {})}
        icon={GaugeIcon}
        label="شهر نزدیک سقف"
        tone="copper"
        value={figure(formatCount(atCap + nearCap))}
      />
      <KpiCard
        icon={HashIcon}
        label="سری فعال"
        tone="forest"
        value={figure(
          usage.activeSeriesPrefix === null ? 'ندارد' : <Digits value={usage.activeSeriesPrefix} />,
        )}
      />
    </section>
  );
}

/** Before any series exists: what to do first, in two lines, and the way there. */
function Onboarding({ manage }: { readonly manage: boolean }) {
  const href = useTabHref();
  return (
    <Notice
      action={
        <Link
          className={cn(buttonVariants(), DARK_PILL)}
          to={manage ? href('settings', { [EDIT_PARAM]: 'series' }) : href('settings')}
        >
          {manage ? 'شروع تنظیم' : 'تنظیمات شماره‌گذاری'}
        </Link>
      }
      description="یک سری شش‌رقمی بسازید و فعالش کنید، بعد برای هر شهر کد و سقف بگذارید. تا آن موقع کارتی صادر نمی‌شود."
      icon={CreditCardIcon}
      title="اول یک سری کارت بسازید"
      tone="gold"
    />
  );
}

const CAP_FILTER = { capState: 'NEAR_CAP,AT_CAP' } as const;

function SummaryPanel({ manage }: { readonly manage: boolean }) {
  const usage = useNumberingUsage();
  const capRows = useAllNumberingCities(CAP_FILTER);
  const failed = usage.status === 'error' ? usage : capRows.status === 'error' ? capRows : null;
  return (
    <QueryState
      error={failed?.error}
      onRetry={() => {
        void usage.refetch();
        void capRows.refetch();
      }}
      skeleton={<Skeleton label="در حال بارگذاری مصرف کارت‌ها…" lines={6} />}
      status={
        failed !== null
          ? 'error'
          : usage.status === 'success' && capRows.status === 'success'
            ? 'success'
            : 'pending'
      }
    >
      {usage.data === undefined || capRows.data === undefined ? null : usage.data.series.length ===
        0 ? (
        <Onboarding manage={manage} />
      ) : (
        <div className="flex flex-col gap-4">
          <SummaryKpis capRows={capRows.data} usage={usage.data} />
          <AttentionList capRows={capRows.data} manage={manage} usage={usage.data} />
        </div>
      )}
    </QueryState>
  );
}
